import React, { useState, useRef, useEffect, useContext } from 'react'; // Add useContext
import { useSearchRefresh } from '../context/SearchRefreshContext'; // Import context
import './MediaUpload.css';
import {
  VStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  useColorModeValue,
  useColorMode,
  useToast,
  HStack,
  Box,
  Flex,
  useBreakpointValue
} from '@chakra-ui/react';
import { UploadSimple, Images, Scan } from '@phosphor-icons/react';
import SubLocationSelector from './media/SubLocationSelector';
import { getAutoDetectedFeatures } from '../services/roboflowService';
import { uploadWithProgress } from '../utils/upload';
import { getOrCreateFeatureFromAutoDetect } from '../firebase/features';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import FeatureTaggingModal from './FeatureTaggingModal';
import MediaUploadActions from './media/MediaUploadActions';
import MediaUploadProgress from './media/MediaUploadProgress';
import MediaPreviewSection from './media/MediaPreviewSection';
import CreditSelector from './media/CreditSelector';
import { AUTO_DETECT_MAPPING } from '../constants/autoDetectMapping';
import { mapFeature, normalizeFeature } from '../utils/featureUtils';
import { validateFileUpload } from '../utils/inputSanitization';

const MediaUpload = ({ onUploadComplete, siteId, media }) => {
  const { triggerDelayedRefresh } = useSearchRefresh(); // Get the delayed refresh trigger
  const toast = useToast();
  const { colorMode } = useColorMode();
  const fileInputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null); // Can be object URL or final URL
  const [imageFile, setImageFile] = useState(null)
  const [detections, setDetections] = useState([]);
  const [filteredDetections, setFilteredDetections] = useState([]);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.5);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('idle');
  const [isUploaded, setIsUploaded] = useState(false);
  const [selectedSubLocationId, setSelectedSubLocationId] = useState(null);
  const [creditInfo, setCreditInfo] = useState({ creditProfileId: null, credits: null });

  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);

  // Responsive layout
  const iconSize = useBreakpointValue({ base: 18, md: 22 });
  const iconWeight = useBreakpointValue({ base: "regular", md: "light" });

  // Theme colors using semantic tokens
  const cardBg = useColorModeValue('bg.surface', 'bg.surface');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const autoBadgeBg = useColorModeValue('brand.primary', 'brand.primary'); // Gold
  const accentColor = useColorModeValue('brand.primary', 'brand.primary'); // Gold
  const textColor = useColorModeValue('text.muted', 'text.muted');
  const headingColor = useColorModeValue('text.default', 'text.default');
  const toastSuccessBorder = useColorModeValue('success', 'success');
  const toastErrorBorder = useColorModeValue('error', 'error');
  const toastInfoBorder = useColorModeValue('info', 'info'); // Gold
  const toastWarningBorder = useColorModeValue('warning', 'warning');
  const toastBg = useColorModeValue('bg.surface', 'bg.surface');

  // Update filtered detections when detections or threshold changes
  useEffect(() => {
    const filtered = detections.filter(det => {
      if (det.manual) return true; // Always include manual detections
      return det.confidence >= confidenceThreshold;
    });
    setFilteredDetections(filtered);
  }, [detections, confidenceThreshold]);

  // Effect for handling resize and updating display size
  useEffect(() => {
    const handleResize = () => {
      // Use imageRef from MediaPreviewSection if possible, or querySelector as fallback
      const img = document.querySelector('img[src^="blob:"]'); // More specific selector
      if (img) {
        const rect = img.getBoundingClientRect();
        // Ensure width/height are positive
        if (rect.width > 0 && rect.height > 0) {
          setDisplaySize({ width: rect.width, height: rect.height });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial call to set size
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [imageSrc]); // Re-run if imageSrc changes

  const handleStartLabelingFeature = () => {
    setIsDrawingEnabled(true);
  };

  const handleNewDetection = (detection) => {
    if (!detection || typeof detection.x !== 'number' || typeof detection.y !== 'number' ||
        typeof detection.width !== 'number' || typeof detection.height !== 'number') {
      console.error('Invalid detection coordinates:', detection);
      return;
    }

    const featureKey = detection.class;
    const newDetection = {
      ...detection,
      manual: true,
      class: featureKey,
      featureId: featureKey,
      // Ensure coordinates are numbers and normalized
      x: Math.max(0, Math.min(1, detection.x)),
      y: Math.max(0, Math.min(1, detection.y)),
      width: Math.max(0, Math.min(1, detection.width)),
      height: Math.max(0, Math.min(1, detection.height))
    };

    setDetections(prev => [...prev, newDetection]);

    // Update features list
    if (featureKey) {
      setSelectedFeatures(prev => ({
        ...prev,
        [featureKey]: {
          enabled: true,
          autoDetected: false,
          confidence: 1,
          label: detection.label || featureKey,
          trainingData: {
            x: newDetection.x,
            y: newDetection.y,
            width: newDetection.width,
            height: newDetection.height,
            imageWidth: naturalSize.width,
            imageHeight: naturalSize.height
          }
        }
      }));
    }
    setIsDrawingEnabled(false);
  };

  const handleFeatureBoxesDrawn = (predictions) => {
    console.log('MediaUpload - Feature boxes drawn:', predictions);
    const validPredictions = predictions.filter(pred =>
      typeof pred.x === 'number' &&
      typeof pred.y === 'number' &&
      typeof pred.width === 'number' &&
      typeof pred.height === 'number'
    ).map(pred => { // pred from FeatureTaggingModal already has class, label, category
      const normalized = {
        class: pred.class, // Standardized ID
        label: pred.label, // Preserve label
        category: pred.category, // Preserve category
        featureId: pred.class, // Redundant, class is the id
        manual: true, // Features from modal are considered manual or manually verified
        confidence: pred.confidence || 1,
        x: Math.max(0, Math.min(1, pred.x)),
        y: Math.max(0, Math.min(1, pred.y)),
        width: Math.max(0, Math.min(1, pred.width)),
        height: Math.max(0, Math.min(1, pred.height))
      };
      console.log('MediaUpload - Normalized prediction from modal:', { original: pred, normalized });
      return normalized;
    });

    setDetections(prev => {
      const updatedDetections = [...prev];
      validPredictions.forEach(newPred => {
        const existingIndex = updatedDetections.findIndex(d =>
          d.class === newPred.class && d.manual === newPred.manual
        );
        if (existingIndex !== -1) {
          updatedDetections[existingIndex] = newPred;
        } else {
          updatedDetections.push(newPred);
        }
      });
      return updatedDetections;
    });

    setSelectedFeatures(prev => {
      const updated = { ...prev };
      for (const det of validPredictions) {
        if (updated[det.class]) {
          updated[det.class] = {
            ...updated[det.class],
            autoDetected: false,
            bbox: { x: det.x, y: det.y, width: det.width, height: det.height }
          };
        }
      }
      return updated;
    });

    toast({
      title: "Feature Boxes Updated",
      description: `Updated ${validPredictions.length} feature(s)`,
      status: "success",
      duration: 3000,
      variant: "subtle",
      containerStyle: { borderWidth: '1px', borderColor: toastSuccessBorder, bg: toastBg },
    });
  };

  const handleDeleteFeature = (index) => {
    const deletedDetection = detections[index];
    const updatedDetections = detections.filter((_, i) => i !== index);
    setDetections(updatedDetections);

    if (deletedDetection) {
      const hasRemainingFeature = updatedDetections.some(d => d.class === deletedDetection.class);
      if (!hasRemainingFeature && selectedFeatures[deletedDetection.class]) {
        setSelectedFeatures(prev => {
          const newFeatures = { ...prev };
          delete newFeatures[deletedDetection.class];
          return newFeatures;
        });
      } else if (hasRemainingFeature) {
        const remainingAutoDetected = updatedDetections.some(d => d.class === deletedDetection.class && !d.manual);
        setSelectedFeatures(prev => ({
          ...prev,
          [deletedDetection.class]: { ...prev[deletedDetection.class], autoDetected: remainingAutoDetected }
        }));
      }
    }

    toast({
      title: "Feature Removed",
      description: "Feature detection has been removed",
      status: "info",
      duration: 3000,
      variant: "subtle",
      containerStyle: { borderWidth: '1px', borderColor: toastInfoBorder, bg: toastBg },
    });
  };

  // Reset function focusing on non-URL state
  const resetOtherState = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setImageFile(null);
    setDetections([]);
    setFilteredDetections([]);
    setUploadProgress(0);
    setError(null);
    setCurrentStep('idle');
    setIsUploaded(false);
    setNaturalSize({ width: 0, height: 0 });
    setDisplaySize({ width: 0, height: 0 });
    setSelectedFeatures({});
    setIsDrawingEnabled(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file upload
      const validation = validateFileUpload(file, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        maxNameLength: 255
      });

      if (!validation.isValid) {
        toast({
          title: "Invalid file", 
          description: validation.error, 
          status: "error", 
          duration: 3000, 
          variant: "subtle",
          containerStyle: { borderWidth: '1px', borderColor: toastErrorBorder, bg: toastBg },
        });
        return;
      }

      // Reset other state fields before processing new file
      resetOtherState();
      // Explicitly clear imageSrc *before* starting file read
      setImageSrc(null); 

      // Set the new file
      setImageFile(file);

      // Use FileReader to get base64 data URL for image preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setImageSrc(dataUrl); // Update state with data URL
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        toast({
          title: "Error Reading File",
          description: "Could not generate image preview.",
          status: "error",
          duration: 3000,
        });
        setImageSrc(null); // Clear preview on error
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    // Ensure dimensions are valid before setting state
    if (naturalWidth > 0 && naturalHeight > 0) {
      setNaturalSize({ width: naturalWidth, height: naturalHeight });
      // Update display size based on loaded image
      const rect = e.target.getBoundingClientRect();
       if (rect.width > 0 && rect.height > 0) {
         setDisplaySize({ width: rect.width, height: rect.height });
       }
    } else {
      console.warn("Image loaded with invalid dimensions:", naturalWidth, naturalHeight);
    }
  };

  const handleScanImage = async () => {
    if (!imageFile) return;
    setIsLoading(true);
    setCurrentStep('detecting');
    try {
      const result = await getAutoDetectedFeatures(imageFile, "2lveGdu961MM2peAj7wg", "megalith-maps", 3);
      const roboflowRawPredictions = result.predictions?.map(pred => ({
        rawClass: pred.class,
        confidence: pred.confidence || 0,
        x: pred.x || 0, y: pred.y || 0,
        width: pred.width || 0, height: pred.height || 0,
        manual: false
      })) || [];

      const enrichedDetections = [];
      const newSelectedFeaturesState = { ...selectedFeatures };

      for (const rawPred of roboflowRawPredictions) {
        if (!rawPred.rawClass || String(rawPred.rawClass).trim() === '') {
          console.warn('Roboflow returned empty or whitespace-only class name. Skipping prediction:', rawPred);
          continue;
        }

        // mapFeature should provide non-empty fallbacks if rawPred.rawClass is valid
        const { featureId: mappedFid, label: mappedLabel, category: mappedCategory } = mapFeature({ class: rawPred.rawClass, source: 'auto' });

        if (!mappedFid || !mappedLabel) {
          console.warn('Could not derive valid feature ID or label for raw class:', rawPred.rawClass, 'Mapped values:', { mappedFid, mappedLabel });
          continue; // Skip if essential info is missing after mapping
        }

        let finalFeatureId = mappedFid;
        let finalLabel = mappedLabel;
        let finalCategory = mappedCategory || 'uncategorized'; // Ensure category has a fallback

        if (rawPred.confidence >= confidenceThreshold) {
          try {
            const featureResult = await getOrCreateFeatureFromAutoDetect(rawPred.rawClass, rawPred.confidence, finalCategory);
            if (featureResult.success && featureResult.data) {
              const dbFeature = featureResult.data;
              // Use DB data only if it's valid
              if (dbFeature.id && String(dbFeature.id).trim() !== '' && dbFeature.label && String(dbFeature.label).trim() !== '') {
                finalFeatureId = dbFeature.id;
                finalLabel = dbFeature.label;
                finalCategory = dbFeature.category || 'uncategorized';
              } else {
                console.warn('Feature from DB has invalid id or label. Using mapped/raw values.', dbFeature);
              }
            }
          } catch (error) {
            console.error('Error syncing feature with Firestore. Using mapped/raw values for selectedFeatures:', error);
          }
          
          // Update selectedFeatures state (used for UI and constructing featuresFromFiltered for upload)
          // Ensure finalFeatureId is valid before using as a key
          if (finalFeatureId && String(finalFeatureId).trim() !== '') {
            newSelectedFeaturesState[finalFeatureId] = {
              enabled: true,
              autoDetected: true,
              confidence: rawPred.confidence,
              confidenceThreshold: confidenceThreshold,
              label: finalLabel, // Already ensured to be non-empty
              category: finalCategory // Already ensured to be non-empty
            };
          } else {
            console.warn("Skipping update to selectedFeatures due to invalid finalFeatureId for rawClass:", rawPred.rawClass);
          }
        }
        
        // Add to enrichedDetections array for UI display and forming 'predictions' list for backend
        // This happens regardless of confidence for initial display; filtering happens via filteredDetections state
        // Ensure all parts are valid before pushing
        if (finalFeatureId && String(finalFeatureId).trim() !== '' && 
            finalLabel && String(finalLabel).trim() !== '' &&
            finalCategory && String(finalCategory).trim() !== '') {
          enrichedDetections.push({
            class: finalFeatureId,
            label: finalLabel,
            category: finalCategory,
            confidence: rawPred.confidence,
            x: rawPred.x, y: rawPred.y,
            width: rawPred.width, height: rawPred.height,
            manual: false
          });
        } else {
            console.warn('Skipping push to enrichedDetections due to invalid data for rawClass:', rawPred.rawClass, {finalFeatureId, finalLabel, finalCategory});
        }
      }

      setDetections(enrichedDetections);
      setSelectedFeatures(newSelectedFeaturesState);

      toast({
        title: "Analysis Complete", 
        description: `Processed ${roboflowRawPredictions.length} raw detections. ${enrichedDetections.length} features added for review.`, 
        status: "success", 
        variant: "subtle",
        containerStyle: { borderWidth: '1px', borderColor: toastSuccessBorder, bg: toastBg }, 
        duration: 3000,
      });
    } catch (error) {
      console.error('Error analyzing file:', error);
      setError(error.message || 'Error analyzing file. Please try again.');
      toast({
        title: "Analysis Failed", description: error.message || "Please try again", status: "error", duration: 3000, variant: "subtle",
        containerStyle: { borderWidth: '1px', borderColor: toastErrorBorder, bg: toastBg },
      });
    } finally {
      setIsLoading(false);
      setCurrentStep('idle');
    }
  };

  const handleUpload = async () => {
    if (!imageFile || isUploaded) return;
    setIsLoading(true);
    setCurrentStep('uploading');
    try {
      let selectedSubLocation = null;
      if (selectedSubLocationId) {
        try {
          const subLocationDoc = await getDoc(doc(db, 'site_sublocations', selectedSubLocationId));
          if (subLocationDoc.exists()) { selectedSubLocation = { id: subLocationDoc.id, ...subLocationDoc.data() }; }
        } catch (error) { console.error('Error fetching sublocation:', error); }
      }

      const featuresFromFiltered = filteredDetections.reduce((acc, det) => {
        const { featureId: mappedFeatureId, label: mappedLabel, category: mappedCategory } = mapFeature({ class: det.class });
        if (mappedFeatureId) {
          acc[mappedFeatureId] = {
            enabled: true,
            autoDetected: !det.manual,
            confidence: det.confidence || 1,
            confidenceThreshold: det.manual ? 0 : confidenceThreshold,
            label: selectedFeatures[mappedFeatureId]?.label || mappedLabel || det.class,
            category: selectedFeatures[mappedFeatureId]?.category || mappedCategory || 'uncategorized'
          };
        }
        return acc;
      }, {});
      const isFirstUpload = !media || media.length === 0;
      const metadata = {
        sublocationId: selectedSubLocationId, 
        coordinates: selectedSubLocation?.coordinates || null, 
        isMain: isFirstUpload,
        credits: creditInfo.credits,
        creditProfileId: creditInfo.creditProfileId,
        // Ensure predictions sent to backend include class, label, and category
        predictions: filteredDetections.map(pred => ({
          class: pred.class, // Standardized ID
          label: pred.label, // Label from detection/modal
          category: pred.category, // Category from detection/modal
          confidence: pred.confidence, 
          x: pred.x, 
          y: pred.y, 
          width: pred.width, 
          height: pred.height, 
          source: pred.manual ? 'manual' : 'auto' 
        })),
        features: featuresFromFiltered,
        imageSize: { width: naturalSize.width, height: naturalSize.height }
      };
      console.log('MediaUpload - Upload metadata (with label & category):', metadata);

      const uploadedFile = await uploadWithProgress(imageFile, siteId, metadata, (progress) => setUploadProgress(progress));

      const finalUrl = uploadedFile.url;
      setImageSrc(finalUrl);

      // The onUploadComplete should receive the same rich prediction data
      onUploadComplete([{
        url: finalUrl || '',
        path: uploadedFile.path || '',
        type: uploadedFile.type || '',
        name: uploadedFile.name || '',
        size: uploadedFile.size || 0,
        uploadedBy: uploadedFile.uploadedBy || 'anonymous',
        sublocationId: selectedSubLocationId,
        coordinates: metadata.coordinates,
        predictions: metadata.predictions,
        isMain: metadata.isMain,
        credits: creditInfo.credits,
        creditProfileId: creditInfo.creditProfileId,
        uploadedAt: uploadedFile.uploadedAt || new Date().toISOString()
      }]);
      
      // Use delayed refresh to allow backend sync to complete
      triggerDelayedRefresh(3000); // 3 second delay to allow Firebase Functions and Elasticsearch sync

      setIsUploaded(true);
      toast({
        title: "Upload Complete", description: "Image and feature data saved successfully", status: "success", duration: 3000, variant: "subtle",
        containerStyle: { borderWidth: '1px', borderColor: toastSuccessBorder, bg: toastBg },
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Error uploading file. Please try again.');
      if (error.warning) {
        toast({
          title: "Upload Partially Complete", description: error.warning, status: "warning", duration: 5000, variant: "subtle",
          containerStyle: { borderWidth: '1px', borderColor: toastWarningBorder, bg: toastBg },
        });
      } else {
        toast({
          title: "Upload Failed", description: error.message || "Please try again", status: "error", duration: 3000, variant: "subtle",
          containerStyle: { borderWidth: '1px', borderColor: toastErrorBorder, bg: toastBg },
        });
      }
      // Do not revoke URL on error, user might retry
    } finally {
      setIsLoading(false);
      setCurrentStep('idle');
    }
  };

  const handleFeatureSelect = (feature) => {
    if (!feature || !feature.id) return;
    const existingAutoDetection = detections.find(d => d.class === feature.id && !d.manual);
    setSelectedFeatures(prev => ({
      ...prev,
      [feature.id]: { enabled: true, autoDetected: existingAutoDetection ? prev[feature.id]?.autoDetected : false, confidence: 1, label: feature.label || feature.id, isCustom: feature.isCustom, bbox: feature.bbox }
    }));

    if (feature.bbox) {
      const newDetection = { class: feature.id, manual: true, confidence: 1, x: feature.bbox.x, y: feature.bbox.y, width: feature.bbox.width, height: feature.bbox.height };
      setDetections(prev => {
        const existingManualIndex = prev.findIndex(d => d.class === feature.id && d.manual === true);
        if (existingManualIndex !== -1) {
          const updated = [...prev];
          updated[existingManualIndex] = newDetection;
          return updated;
        }
        return [...prev, newDetection];
      });
    }
  };

  const handleConfidenceChange = (newThreshold) => {
    setConfidenceThreshold(newThreshold);
  };

  return (
    <Card
      variant="parchment" border="1px" borderColor={borderColor} borderRadius="lg" boxShadow="sm" overflow="hidden" transition="all 0.2s" _hover={{ boxShadow: "md" }}
    >
      <CardHeader pb={2}>
        <Flex align="center" gap={2} mb={2}>
          <Images size={iconSize} weight={iconWeight} color={accentColor} />
          <Heading size="md" textStyle="carved" color={headingColor}>Upload Site Media</Heading> {/* Use semantic token */}
        </Flex>
        <Text color={textColor} fontSize="sm" textStyle="papyrus">Upload images for archaeological feature analysis using AI</Text>
      </CardHeader>

      <CardBody>
        <VStack spacing={6} align="stretch">
          <MediaUploadActions
            fileInputRef={fileInputRef} imageFile={imageFile} isLoading={isLoading} isUploaded={isUploaded} detections={detections}
            onSelectClick={() => fileInputRef.current?.click()} onScanClick={handleScanImage} onUploadClick={handleUpload}
            siteId={siteId} selectedSubLocationId={selectedSubLocationId} onSubLocationSelect={(selection) => setSelectedSubLocationId(selection.subLocationId)}
            accentColor={accentColor}
          />

          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />

          <MediaUploadProgress currentStep={currentStep} uploadProgress={uploadProgress} error={error} />

          {imageSrc && (
            <Box>
              <CreditSelector onCreditChange={setCreditInfo} />
            </Box>
          )}

          <MediaPreviewSection
            imageSrc={imageSrc} displaySize={displaySize} naturalSize={naturalSize} detections={filteredDetections} selectedFeatures={selectedFeatures}
            isUploaded={isUploaded} autoBadgeBg={autoBadgeBg} handleImageLoad={handleImageLoad}
            onAddFeatureClick={() => setIsFeatureModalOpen(true)} onNewDetection={handleNewDetection} onStartLabelingFeature={handleStartLabelingFeature}
            onDeleteFeature={handleDeleteFeature} setDetections={setDetections} accentColor={accentColor}
            confidenceThreshold={confidenceThreshold} onConfidenceChange={handleConfidenceChange}
          />
        </VStack>
      </CardBody>

      <FeatureTaggingModal
        isOpen={isFeatureModalOpen} onClose={() => setIsFeatureModalOpen(false)} onFeatureSelect={handleFeatureSelect} onFeatureBoxesDrawn={handleFeatureBoxesDrawn}
        existingFeatures={Object.keys(selectedFeatures)}
        currentPredictions={detections.map(d => ({ class: d.class, source: d.manual ? 'manual' : 'auto', confidence: d.confidence || 0.99, x: d.x, y: d.y, width: d.width, height: d.height }))}
        isLoading={isLoading}
        media={{
          url: imageSrc, name: imageFile?.name, type: 'image',
          predictions: filteredDetections.map(d => ({ class: d.class, source: d.manual ? 'manual' : 'auto', confidence: d.confidence || 0.99, x: d.x, y: d.y, width: d.width, height: d.height }))
        }}
      />
    </Card>
  );
};

export default MediaUpload;
